'use client'

import { useState, useEffect, useMemo } from 'react'
import { useTheme } from 'next-themes'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useMounted } from '@/lib/opendocs/hooks/use-mounted'
import { useMediaQuery } from '@/lib/opendocs/hooks/use-media-query'
import { Button } from '@/components/ui/button'
import { RibbonCanvas } from '@/components/fx/ribbon-canvas'
import { SolutionsCarousel } from '@/components/solutions-carousel'

/* ─── Hero ribbon: big flowing strand field, rotated −28°, faded at both
   strand ends. Colours come from RibbonCanvas defaults (brand mint→sky). ── */
const HERO_RIBBON = {
  speed: 0.66,
  rotate: -28,
  edgeFade: 0.25,
  background: 'transparent',
}

/* ─── Circuit layer (after the Framer reference): long rails crossing the
   canvas, large rounded "component" outlines, big sweeping-radius
   connectors between them, and hollow junction nodes sitting on the lines
   where they meet. One connected system, kept left of the ribbon's sweep. ── */
const CIRCUIT_RECTS: {
  x: number
  y: number
  w: number
  h: number
  r: number
}[] = [
  // wide pill component, top-left
  { x: 80, y: 72, w: 340, h: 64, r: 32 },
  // tall component right of it, dropping toward the headline gap
  { x: 470, y: 96, w: 120, h: 184, r: 28 },
  // bottom-right cluster: tall component + wide pill under it
  { x: 1240, y: 560, w: 120, h: 184, r: 28 },
  { x: 1020, y: 800, w: 280, h: 64, r: 32 },
]

const CIRCUIT_TRACES: {
  d: string
  /** linearGradient id for rails whose free ends must fade out smoothly */
  grad?: string
}[] = [
  // top rail crossing the whole band — fades out at both free ends
  { d: 'M40,44 H900', grad: 'circuit-rail-top' },
  // rail → pill drop
  { d: 'M250,44 V72' },
  // pill → tall component, one big sweeping connector
  { d: 'M150,136 V184 a44,44 0 0 0 44,44 H470' },
  // tall component → back up into the rail
  { d: 'M590,140 H660 a36,36 0 0 0 36,-36 V44' },
  // short vertical rail from the nav area
  { d: 'M770,20 V210' },
  // left rail past the text column, sweeping inward at the bottom
  { d: 'M64,240 V560 a40,40 0 0 0 40,40 H128' },
  { d: 'M64,380 H100' },
  // bottom-left sweep — fades in from its free start
  {
    d: 'M40,704 H240 a56,56 0 0 1 56,56 V808',
    grad: 'circuit-sweep-bl',
  },
  // bottom-right cluster: vertical rail into the tall component…
  { d: 'M1396,480 V900' },
  { d: 'M1396,600 H1360' },
  // …then a sweeping connector down into the pill
  { d: 'M1280,744 V768 a32,32 0 0 1 -32,32' },
  { d: 'M1020,832 H980' },
]

/** Hollow junction nodes that sit ON lines/outlines where they connect. */
const CIRCUIT_NODES: [number, number][] = [
  [250, 44],
  [150, 136],
  [470, 228],
  [696, 44],
  [770, 214],
  [64, 234],
  [134, 600],
  [1396, 474],
  [1360, 600],
  [1248, 800],
]

const CIRCUIT_DOTS: [number, number][] = [
  [106, 380],
  [974, 832],
]

// The root color feeds the gradient stops' currentColor — keep it in sync
// with the stroke classes on the plain traces below.
// Desktop only: the layout is composed against a 1440-wide band, and
// `slice` blows it up on phones until a single "component" outline spans the
// whole headline. Below md the ribbon carries the hero on its own.
const CircuitTraces = () => (
  <svg
    aria-hidden
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMin slice"
    fill="none"
    className="pointer-events-none absolute inset-0 hidden size-full text-emerald-600/15 md:block dark:text-primary-active/[0.07]"
  >
    <defs>
      {/* End fades for rails with free ends (userSpaceOnUse = same coords
          as the paths). currentColor carries the theme-aware trace color. */}
      <linearGradient
        id="circuit-rail-top"
        gradientUnits="userSpaceOnUse"
        x1="40"
        y1="0"
        x2="900"
        y2="0"
      >
        <stop offset="0" stopColor="currentColor" stopOpacity="0" />
        <stop offset="0.08" stopColor="currentColor" stopOpacity="1" />
        <stop offset="0.9" stopColor="currentColor" stopOpacity="1" />
        <stop offset="1" stopColor="currentColor" stopOpacity="0" />
      </linearGradient>
      <linearGradient
        id="circuit-sweep-bl"
        gradientUnits="userSpaceOnUse"
        x1="40"
        y1="0"
        x2="296"
        y2="0"
      >
        <stop offset="0" stopColor="currentColor" stopOpacity="0" />
        <stop offset="0.3" stopColor="currentColor" stopOpacity="1" />
      </linearGradient>
    </defs>
    {CIRCUIT_TRACES.map(({ d, grad }) => (
      <path
        key={d}
        d={d}
        strokeWidth={1}
        stroke={grad ? `url(#${grad})` : undefined}
        className={
          grad
            ? undefined
            : 'stroke-emerald-600/15 dark:stroke-primary-active/[0.07]'
        }
      />
    ))}
    {CIRCUIT_RECTS.map((r) => (
      <rect
        key={`${r.x}-${r.y}`}
        x={r.x}
        y={r.y}
        width={r.w}
        height={r.h}
        rx={r.r}
        strokeWidth={1}
        className="stroke-emerald-600/15 dark:stroke-primary-active/[0.07]"
      />
    ))}
    {CIRCUIT_NODES.map(([cx, cy]) => (
      <circle
        key={`${cx}-${cy}`}
        cx={cx}
        cy={cy}
        r={7}
        strokeWidth={1}
        className="fill-background stroke-emerald-600/20 dark:stroke-primary-active/10"
      />
    ))}
    {CIRCUIT_DOTS.map(([cx, cy]) => (
      <circle
        key={`${cx}-${cy}`}
        cx={cx}
        cy={cy}
        r={3}
        className="fill-emerald-600/15 dark:fill-primary-active/10"
      />
    ))}
  </svg>
)

/* ─── Mesh glow (after the Framer reference): blurred diagonal light beams
   in brand mint/sky. The field extends PAST the fold: the beams peak just
   above it and dissolve both ways — up into the hero and, crucially, down
   behind the solutions header and the top half of the "Every Enterprise…"
   card, so the glow never ends on a hard line above the section. It fades
   to transparent (not the background token), revealing the page beneath. ── */
// True mesh: irregular radial color blobs (position/size/rotation all vary —
// nothing tiles), blurred into one continuous field.
const MESH_BLOBS = [
  { cx: 170, cy: 430, rx: 430, ry: 250, rot: 0, fill: 'mint' },
  { cx: 560, cy: 540, rx: 380, ry: 215, rot: -14, fill: 'sky' },
  { cx: 900, cy: 420, rx: 330, ry: 245, rot: 8, fill: 'teal' },
  { cx: 1190, cy: 560, rx: 450, ry: 255, rot: -6, fill: 'mint' },
  { cx: 1420, cy: 390, rx: 300, ry: 200, rot: 12, fill: 'sky' },
]

// How far past the fold the field reaches. ~150svh lands its dissolve around
// the top half of the solutions card on typical viewports — nudge this (and
// the SVG height % below with it) to move where the green trails off.
const MeshGlow = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[150svh] overflow-hidden"
    style={{
      // Belt-and-braces: even if the gradients still carry a whisper of colour
      // at the very bottom, feather the whole field out over its last stretch
      // so there is never a hard bottom edge behind the card.
      WebkitMaskImage:
        'linear-gradient(to bottom, black 0%, black 88%, transparent 100%)',
      maskImage:
        'linear-gradient(to bottom, black 0%, black 88%, transparent 100%)',
      /* This field is a large, heavily blurred, completely STATIC image, and
         it sits directly under a canvas that repaints continuously. Left in
         the shared layer it was re-rasterized — gaussian blur and all — on
         the ribbon's frames. Its own layer is painted once and then only
         composited. */
      transform: 'translateZ(0)',
      contain: 'paint',
    }}
  >
    <svg
      viewBox="0 0 1440 636"
      preserveAspectRatio="none"
      className="absolute inset-x-0 bottom-0 h-[73%] w-full opacity-80 dark:opacity-30"
    >
      <defs>
        {/* Each blob is a soft radial falloff; overlapping falloffs of
            different hues are what makes it read as a mesh gradient. */}
        <radialGradient id="mesh-blob-mint">
          <stop offset="0" stopColor="#37ecb0" stopOpacity="0.5" />
          <stop offset="0.55" stopColor="#37ecb0" stopOpacity="0.18" />
          <stop offset="1" stopColor="#37ecb0" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="mesh-blob-sky">
          <stop offset="0" stopColor="#0ea5e9" stopOpacity="0.42" />
          <stop offset="0.55" stopColor="#0ea5e9" stopOpacity="0.15" />
          <stop offset="1" stopColor="#0ea5e9" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="mesh-blob-teal">
          <stop offset="0" stopColor="#10b981" stopOpacity="0.38" />
          <stop offset="0.55" stopColor="#10b981" stopOpacity="0.13" />
          <stop offset="1" stopColor="#10b981" stopOpacity="0" />
        </radialGradient>
        {/* Tint band (top→bottom of the field): carries the mint wash down
            through the fold and dissolves to transparent behind the card. */}
        <linearGradient id="mesh-band" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#37ecb0" stopOpacity="0" />
          <stop offset="0.3" stopColor="#37ecb0" stopOpacity="0.2" />
          <stop offset="0.54" stopColor="#37ecb0" stopOpacity="0.16" />
          <stop offset="0.78" stopColor="#37ecb0" stopOpacity="0.08" />
          <stop offset="1" stopColor="#37ecb0" stopOpacity="0" />
        </linearGradient>
        <filter id="mesh-blur" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="55" />
        </filter>
      </defs>
      <g filter="url(#mesh-blur)">
        {MESH_BLOBS.map(({ cx, cy, rx, ry, rot, fill }, i) => (
          <ellipse
            key={i}
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            transform={`rotate(${rot} ${cx} ${cy})`}
            fill={`url(#mesh-blob-${fill})`}
          />
        ))}
      </g>
      <rect x="0" y="0" width="1440" height="636" fill="url(#mesh-band)" />
    </svg>
  </div>
)

/* ─── Types ──────────────────────────────────────────────────────────── */
interface HeroModernProps {
  title?: string
  /* Required. The default used to be a database vendor's own subhead, which
     would have shipped as Euclideum's voice on any page that forgot to pass
     one. Making it required turns that into a compile error instead. */
  description: string
  primaryButtonText?: string
  secondaryButtonText?: string
  primaryButtonUrl?: string
  secondaryButtonUrl?: string
}

/* ─── Static data ────────────────────────────────────────────────────── */
// `lightArt` marks logos drawn in white — they need the opposite filter
// polarity per theme to stay visible.
//
// Order matters: the rail is a loop, so neighbours here are neighbours on
// screen forever. The two Credentials marks and the four D Y Patil marks are
// spaced out so no two of the same family ever share an edge.
const logos: { src: string; name: string; lightArt?: boolean }[] = [
  // { src: "/stories/IIMA.webp", name: "IIM Ahmedabad" },
  // { src: "/stories/newICTM.webp", name: "ICTM" },
  {
    src: '/stories/walchandlog.webp',
    name: 'Walchand College of Engineering & Technology',
  },
  {
    src: '/stories/dyp.webp',
    name: 'D Y Patil College of Engineering & Technology',
  },
  { src: '/stories/campus-credentials.webp', name: 'Campus Credentials' },
  {
    src: '/stories/dyptc.webp',
    name: 'D Y Patil Technical Campus',
    lightArt: true,
  },
  { src: '/stories/kjsomaiya.webp', name: 'K J Somaiya School of Engineering' },
  {
    src: '/images/dyp-logo.webp',
    name: 'D Y Patil Education Society (Deemed to be University), Kolhapur',
  },
  { src: '/stories/career.webp', name: 'Career Credentials', lightArt: true },
  {
    src: '/stories/dypATU.webp',
    name: 'D Y Patil Agriculture & Technical University',
  },
  {
    src: '/stories/stjohn.webp',
    name: 'St. John College of Engineering & Management',
  },
  { src: '/stories/pillai.webp', name: 'Pillai University' },
]

/* ─── HeroModern ─────────────────────────────────────────────────────── */
const HeroModern = ({
  title,
  description,
  primaryButtonText = 'Get Started',
  primaryButtonUrl = '#',
  secondaryButtonText = 'Learn More',
}: HeroModernProps) => {
  const mounted = useMounted()
  const { theme, resolvedTheme } = useTheme()
  const [isDark, setIsDark] = useState(true)

  useEffect(() => {
    if (!mounted) return
    const id = setTimeout(() => {
      setIsDark((resolvedTheme ?? theme ?? 'dark') === 'dark')
    }, 0)
    return () => clearTimeout(id)
  }, [theme, resolvedTheme, mounted])

  /* Held stable across renders. As an inline object literal this was a new
     identity every render, so RibbonCanvas re-ran the memo and the effect that
     syncs its settings ref on every parent render — including every one of
     these theme ticks. */
  const ribbonSettings = useMemo(
    () => ({ ...HERO_RIBBON, opacity: isDark ? 0.55 : 0.9 }),
    [isDark]
  )

  /* Must match the `md:hidden` on the static wave below — if the two
     breakpoints ever disagree you get both fields at once, or neither.
     Starts false, so the server render and first paint carry no canvas and
     phones never create one at all. */
  const ribbonEnabled = useMediaQuery('(min-width: 768px)')

  return (
    <section
      className={cn(
        'relative min-h-[100svh] overflow-hidden hero-adaptive-bg',
        // The site header is transparent over the hero and only frosts once
        // scrolled, so the hero canvas has to start at the true top of the
        // viewport rather than below the header's box in flow. Pulling it up
        // by exactly the header height closes the band of bare page
        // background that otherwise sat above it.
        'mt-[calc(var(--header-h)*-1)]'
      )}
      suppressHydrationWarning
    >
      {/* Dark-mode stage: the hero opens on the approved near-black and
          dissolves gradually into the page canvas — a long fade, not a
          section-boundary color switch. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 hidden h-[150svh] dark:block"
        style={{
          background:
            'linear-gradient(to bottom, #09090b 0%, #09090b 65%, transparent 100%)',
          // Static backdrop under an animating canvas — same reasoning as the
          // mesh field above.
          transform: 'translateZ(0)',
        }}
      />
      <MeshGlow />
      {/* Flowing ribbon field behind the headline — clipped to the first
          viewport and dissolved out before the solutions block so it never
          presents a hard bottom edge. */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-0 h-[100svh] overflow-hidden',
          // Phones have no free column beside the copy, so the field is
          // graded by height instead: a whisper above the stack, held down to
          // ~10% behind the copy so nothing stripes the headline, then opened
          // to full strength below the buttons where it becomes the band that
          // carries the bottom of the fold.
          '[mask-image:linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.45)_12%,rgba(0,0,0,0.12)_22%,rgba(0,0,0,0.1)_62%,black_81%,black_95%,transparent_100%)]',
          '[-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.45)_12%,rgba(0,0,0,0.12)_22%,rgba(0,0,0,0.1)_62%,black_81%,black_95%,transparent_100%)]',
          // From md the copy sits in the left half and the field can run the
          // full height beside it again.
          'md:[mask-image:linear-gradient(to_bottom,transparent_0%,black_12%,black_80%,transparent_98%)]',
          'md:[-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,black_12%,black_80%,transparent_98%)]'
        )}
      >
        <CircuitTraces />

        {/* Phones get the strand field as a flat drawing instead of a live
            WebGL canvas. It is the same artwork, and on a phone the animated
            version was the single most expensive thing on the page — a
            full-width canvas repainting every frame on exactly the hardware
            least able to afford it.

            Delivered as a background-image, not an <img>, on purpose: a
            display:none background is never fetched, so desktop does not pay
            for a file it will not draw. */}
        {/* bg-cover, not bg-contain: `contain` fits the whole drawing inside
            the box, so any mismatch between the artwork's aspect and the
            phone's left slack — a bare strip beside the wave on tall handsets
            and above it on short ones. `cover` fills the box on every screen
            and lets the overflow crop instead. Anchored bottom-right so the
            fan that closes the composition always meets the corner; the crop
            is taken off the top-left, where the field is sparsest. */}
        {/* Preload hint, hoisted to <head> by React: a CSS background-image
            is only discovered after stylesheets resolve, which made this wave
            the last-arriving piece of the phone hero (Lighthouse flagged the
            LCP resource as not discoverable in the initial document). The
            media condition keeps desktop from fetching a file it never
            draws. */}
        <link
          rel="preload"
          as="image"
          href="/illustrations/wave-graphic.svg"
          media="(max-width: 767px)"
          fetchPriority="high"
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[58svh] bg-[url('/illustrations/wave-graphic.svg')] bg-cover bg-right-bottom bg-no-repeat md:hidden"
        />

        {/* The live ribbon is mounted only from md up. Hiding it with CSS
            would not have helped: the component still creates a WebGL context
            and runs its animation loop when it is merely invisible. */}
        {ribbonEnabled && (
          <RibbonCanvas
            settings={ribbonSettings}
          /* The ribbon is a slow ambient drift (speed 0.66), not motion the
             eye tracks — at 30fps it is indistinguishable from 60, for half
             the GPU and main-thread frame cost. The DPR cap comes down with
             it: this canvas is a soft, masked, out-of-focus field, so the
             extra backing-store pixels bought nothing visible while costing
             ~1.4x the fragment work every frame. */
          maxFps={30}
            maxDpr={1.25}
            className={cn(
              // Pushed right of the text column. The base `left-1/2` and the
              // unprefixed mask are the fallback md: overrides sit on top of;
              // this only ever renders at >=md.
              'absolute inset-y-0 left-1/2 w-full max-w-[1920px] -translate-x-1/2 md:left-[62%]',
              // fade the bundle out toward the left so the text column stays clean
              '[mask-image:linear-gradient(to_right,transparent_0%,black_20%)]',
              '[-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_20%)]',
              'md:[mask-image:linear-gradient(to_right,transparent_8%,black_42%)]',
              'md:[-webkit-mask-image:linear-gradient(to_right,transparent_8%,black_42%)]'
            )}
          />
        )}
      </div>
      <div className="container mx-auto relative z-10" suppressHydrationWarning>
        {/* Left-aligned hero stack (leaf-style): announcement pill →
            headline → description → actions. The right half stays open so
            the ribbon field can breathe. */}
        {/* Full viewport stage. Top and bottom padding are both one header
            height, so justify-center lands the stack on the exact vertical
            centre of the viewport while still guaranteeing it can never run
            under the header. */}
        <div
          className={cn(
            'flex min-h-[100svh] flex-col items-start justify-center',
            // Extra bottom padding on phones lifts the stack off the centre
            // line, clearing the lower band for the ribbon.
            'pt-[var(--header-h)] pb-[24svh] md:py-[var(--header-h)]'
          )}
          suppressHydrationWarning
        >
          <h1
            className={cn(
              'font-heading max-w-4xl font-normal tracking-tight',
              // Line height is pinned to each font-size step with the `/`
              // shorthand: a bare `leading-*` loses to the line-height baked
              // into `text-5xl`/`text-6xl`, whose media queries are emitted
              // later, which was collapsing the headline to 1.0 leading.
              'text-[32px]/[1.15] sm:text-5xl/[1.15] md:text-6xl/[1.15] lg:text-[68px]/[1.15]',
              'text-foreground dark:text-white'
            )}
          >
            {/* Rendered as plain static text — the ChromaText sweep delayed
                the largest contentful paint on the page for a purely
                decorative effect, so the headline now paints with the first
                frame. */}
            Shaping the Future of{' '}
            {/* Phone-width break: without it the line falls after "Learning",
                orphaning "& Enterprises" and splitting the two underlined
                terms across lines. */}
            <br className="sm:hidden" />
            <span
              className={cn(
                'relative inline-block',
                'after:absolute after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-[#37ecb0]',
                'after:-bottom-1.5 sm:after:-bottom-2.5'
              )}
            >
              Learning
            </span>{' '}
            &{' '}
            <span
              className={cn(
                'relative inline-block',
                'after:absolute after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-[#37ecb0]',
                'after:-bottom-1.5 sm:after:-bottom-2.5'
              )}
            >
              Enterprises
            </span>
          </h1>

          <p className="mt-5 max-w-2xl text-pretty text-[17px] leading-[1.55] sm:mt-6 sm:text-lg sm:leading-relaxed md:mt-7 text-[#2B3A46] dark:text-[#DEE9EE]/90">
            {description}
          </p>

          {/* Side by side on phones too: two full-width stacked blocks ate a
              third of the fold and read as a form, not a hero. */}
          <div className="mt-7 flex w-full flex-row items-center gap-3 sm:mt-8 sm:w-auto md:mt-10">
            <Button
              asChild
              className="h-12 flex-1 px-5 text-sm sm:flex-none sm:px-8 sm:text-base"
            >
              <a href={primaryButtonUrl}>{primaryButtonText}</a>
            </Button>
            <Button
              variant="outline"
              asChild
              className="h-12 flex-1 px-5 text-sm sm:flex-none sm:px-8 sm:text-base"
            >
              <a href="#solutions">{secondaryButtonText}</a>
            </Button>
          </div>
        </div>
      </div>

      <SolutionsCarousel />

      {/* ── Trusted Teams Banner — centered statement over a full-bleed
          hairline logo rail ── */}
      <section
        id="solutions"
        className={cn('w-full', 'bg-background')}
        suppressHydrationWarning
      >
        {/* Statement block: breathing room without the void. */}
        <div className="mx-auto w-full max-w-4xl px-6 py-14 text-center sm:py-16 lg:py-20">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground sm:text-sm">
            Trusted by teams at
          </p>
          <h2
            className={cn(
              'font-heading mx-auto mt-4 max-w-3xl text-3xl font-normal leading-[1.2] tracking-tight',
              'sm:text-4xl md:text-5xl lg:max-w-none lg:text-[52px]',
              'text-foreground dark:text-white'
            )}
          >
            We help institutions run learning, operations, and growth on one
            unified platform.
          </h2>
        </div>

        {/* The rail runs edge to edge: hairlines top and bottom, one 1px
            divider between cells, no fade mask — the grid meets the
            viewport edge cleanly. */}
        {/* Two copies, not four. The loop translates by -50%, so the second
            half only has to match the first — the extra pair was 20 more
            <Image> elements, each carrying a filter and a blend mode, inside
            a single always-animating compositor layer twice as wide as it
            needed to be. */}
        <div className="trust-strip w-full overflow-hidden border-y border-border/70">
          <div className="trust-track flex w-max">
            {[...logos, ...logos].map((logo, index) => (
              <div
                key={index}
                aria-hidden={index >= logos.length}
                className={cn(
                  'flex shrink-0 items-center justify-center',
                  'border-r border-border/70',
                  'h-24 w-44 sm:h-32 sm:w-60 md:h-36 md:w-72 lg:h-44 lg:w-[19rem]'
                )}
              >
                <div className="relative h-10 w-28 sm:h-12 sm:w-40 md:h-16 md:w-48 lg:h-20 lg:w-56">
                  <Image
                    src={logo.src}
                    alt={index < logos.length ? logo.name : ''}
                    fill
                    quality={95}
                    sizes="(max-width: 640px) 112px, (max-width: 768px) 160px, (max-width: 1024px) 192px, 224px"
                    className={cn(
                      'trust-logo object-contain',
                      logo.lightArt && 'trust-logo--light-art'
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <style jsx global>{`
          /* Off-screen the rail costs nothing: the browser skips rendering the
             subtree entirely, which also stops the marquee's animation from
             being composited while it cannot be seen. The reserved size keeps
             the page from reflowing as it enters and leaves. */
          .trust-strip {
            content-visibility: auto;
            contain-intrinsic-size: auto 11rem;
          }
          .trust-track {
            animation: trust-scroll 70s linear infinite;
            will-change: transform;
          }
          .trust-strip:hover .trust-track {
            animation-play-state: paused;
          }
          @keyframes trust-scroll {
            from {
              transform: translateX(0);
            }
            to {
              transform: translateX(-50%);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .trust-track {
              animation: none;
            }
          }
          /* Single-ink logo wall: grayscale + blend keeps crest detail
             legible where a brightness(0) silhouette would crush it. */
          .trust-logo {
            filter: grayscale(1);
            mix-blend-mode: multiply;
            opacity: 0.88;
          }
          .dark .trust-logo {
            filter: grayscale(1) invert(1);
            mix-blend-mode: screen;
          }
          /* White-drawn marks flip polarity so they read on both themes. */
          .trust-logo--light-art {
            filter: grayscale(1) invert(1);
          }
          .dark .trust-logo--light-art {
            filter: grayscale(1);
          }
        `}</style>
      </section>
    </section>
  )
}

export { HeroModern }
